import {
    collection,
    collectionGroup,
    getCountFromServer,
    getDocs,
    getDoc,
    doc,
    limit,
    orderBy,
    query,
    Timestamp,
    where,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import type { FeedbackData } from '../../../types/feedback';
import type { ObsessLessUser } from '../../../types/commonTypes';
import { accountDeletionsService } from '../../AccountDeletions/services/accountDeletionsService';
import type { DeletionHealthCounts } from '../../AccountDeletions/types/accountDeletionTypes';
import type { ReferralCodeData } from '../../ReferralCodes/services/referralCodesService';
import type { UserTriggerLogStats } from '../../Stats/services/statsService';

export interface UserGrowthMonthData {
    month: string;
    displayMonth: string;
    count: number;
}

class DashboardService {
    private static instance: DashboardService;

    private constructor() {}

    public static getInstance(): DashboardService {
        if (!DashboardService.instance) {
            DashboardService.instance = new DashboardService();
        }
        return DashboardService.instance;
    }

    private convertTimestampToDate(timestamp: unknown): Date {
        if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
            return (timestamp as Timestamp).toDate();
        }
        if (typeof timestamp === 'string') return new Date(timestamp);
        if (typeof timestamp === 'number') return new Date(timestamp * 1000);
        return new Date();
    }

    async getLatestFeedbacks(count = 3): Promise<FeedbackData[]> {
        const q = query(
            collection(db, 'user_feedback'),
            orderBy('createdAt', 'desc'),
            limit(count)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as FeedbackData));
    }

    async getTopQuestJourneyUsers(count = 3): Promise<ObsessLessUser[]> {
        const q = query(
            collection(db, 'user_info'),
            orderBy('questJourney.currentDay', 'desc'),
            limit(count)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs
            .filter((d) => d.data().questJourney)
            .map((d) => {
                const data = d.data();
                return {
                    uid: d.id,
                    email: data.email ?? null,
                    displayName: data.displayName ?? null,
                    createdAt: this.convertTimestampToDate(data.createdAt),
                    hasFinishedOnboarding: data.hasFinishedOnboarding ?? false,
                    onboardingProgress: data.onboardingProgress ?? '',
                    referralCode: data.referralCode,
                    questJourney: data.questJourney
                        ? {
                              createdAt: this.convertTimestampToDate(data.questJourney.createdAt),
                              currentDay: data.questJourney.currentDay,
                              isCurrentDayCompleted: data.questJourney.isCurrentDayCompleted,
                              journeyDayStartedAt: this.convertTimestampToDate(
                                  data.questJourney.journeyDayStartedAt
                              ),
                              updatedAt: this.convertTimestampToDate(data.questJourney.updatedAt),
                              journeyDayCompletedAt: data.questJourney.journeyDayCompletedAt
                                  ? this.convertTimestampToDate(
                                        data.questJourney.journeyDayCompletedAt
                                    )
                                  : undefined,
                          }
                        : undefined,
                } as ObsessLessUser;
            });
    }

    async getTopTriggerLogUsers(count = 3): Promise<UserTriggerLogStats[]> {
        const snapshot = await getDocs(collectionGroup(db, 'trigger_logs'));

        const countsByUser = new Map<string, number>();
        snapshot.docs.forEach((d) => {
            const userId = d.ref.parent.parent?.id;
            if (userId) {
                countsByUser.set(userId, (countsByUser.get(userId) || 0) + 1);
            }
        });

        const topUserIds = Array.from(countsByUser.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, count)
            .map(([userId, triggerLogCount]) => ({ userId, triggerLogCount }));

        const userStatsPromises = topUserIds.map(async ({ userId, triggerLogCount }) => {
            const userDoc = await getDoc(doc(db, 'user_info', userId));
            if (!userDoc.exists()) {
                return { userId, email: null, displayName: null, triggerLogCount, memberSince: new Date() };
            }
            const data = userDoc.data();
            return {
                userId,
                email: data?.email ?? null,
                displayName: data?.displayName ?? null,
                triggerLogCount,
                memberSince: this.convertTimestampToDate(data?.createdAt),
            } satisfies UserTriggerLogStats;
        });

        return Promise.all(userStatsPromises);
    }

    async getUserGrowthLast3Months(): Promise<UserGrowthMonthData[]> {
        const now = new Date();
        const months: UserGrowthMonthData[] = [];

        const countPromises = Array.from({ length: 3 }, (_, i) => {
            const monthOffset = 2 - i; // 2, 1, 0 => oldest first
            const start = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
            const end = new Date(now.getFullYear(), now.getMonth() - monthOffset + 1, 1);

            const q = query(
                collection(db, 'user_info'),
                where('createdAt', '>=', Timestamp.fromDate(start)),
                where('createdAt', '<', Timestamp.fromDate(end))
            );

            const displayMonth = start.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
            const monthKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;

            return getCountFromServer(q).then((snap) => ({
                month: monthKey,
                displayMonth,
                count: snap.data().count,
            }));
        });

        const results = await Promise.all(countPromises);
        months.push(...results);
        return months;
    }

    /**
     * Delegated rather than reimplemented: the query shapes there are pinned to the
     * indexes that exist, and a second copy here would drift from them.
     */
    async getAccountDeletionHealth(): Promise<DeletionHealthCounts> {
        return accountDeletionsService.getHealthCounts();
    }

    async getLatestUpdatedReferralCodes(count = 3): Promise<ReferralCodeData[]> {
        const q = query(
            collection(db, 'referral_codes'),
            orderBy('updatedAt', 'desc'),
            limit(count)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => {
            const data = d.data();
            return {
                code: data.code,
                discountPercent: data.discountPercent,
                isActive: data.isActive,
                usesCount: data.usesCount,
                createdAt: this.convertTimestampToDate(data.createdAt),
                expiresAt: data.expiresAt ? this.convertTimestampToDate(data.expiresAt) : null,
                revenueSharePercent: data.revenueSharePercent,
                applyResultMessage: data.applyResultMessage,
            } as ReferralCodeData;
        });
    }
}

export const dashboardService = DashboardService.getInstance();
